import { Link } from "react-router-dom";
import { Check } from "@phosphor-icons/react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";

const PLANS = [
  {
    key: "small",
    name: "Small",
    price: "$50",
    limit: "Up to 30 active members",
    highlight: false,
  },
  {
    key: "medium",
    name: "Medium",
    price: "$75",
    limit: "Up to 60 active members",
    highlight: true,
  },
  {
    key: "large",
    name: "Large",
    price: "$90",
    limit: "Unlimited active members",
    highlight: false,
  },
];

const COMMON_FEATURES = [
  "Custom pass designer",
  "Unlimited pass designs",
  "Google Wallet issuance",
  "CSV member import",
  "Membership expiry tracking",
];

const FAQS = [
  {
    q: "Do I need a credit card to try it?",
    a: "No. You can sign in, design your pass, and issue up to 5 test passes completely free — no card required. Test passes are watermarked until you subscribe.",
  },
  {
    q: "Is pricing per member?",
    a: "No — it's a single flat annual fee per club based on how many active members you have, with no per-member charges on top.",
  },
  {
    q: "Can I switch plans later?",
    a: "Yes. You can upgrade or downgrade at any time from your billing settings, and you're only charged or credited the prorated difference immediately — no need to cancel and re-subscribe.",
  },
  {
    q: "Is GST included?",
    a: "Prices are shown GST-exclusive, as Airsoft Ventures Limited is currently under New Zealand's GST registration threshold.",
  },
];

const PricingPage = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <PublicNav />

      <main className="flex-1">
        <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-12 sm:pt-24 sm:pb-16">
          <p className="eyebrow mb-5">Pricing</p>
          <h1 className="font-display text-[44px] sm:text-6xl leading-[1.04] font-semibold text-zinc-900 max-w-3xl">
            Simple, flat-rate pricing.
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-zinc-600 max-w-2xl leading-relaxed">
            Billed annually in NZD. No per-member fees, no surprises. Try it free before you subscribe.
          </p>
        </section>

        <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-20 sm:pb-28">
          <div className="grid grid-cols-1 md:grid-cols-3 border border-zinc-200 rounded-2xl overflow-hidden divide-y md:divide-y-0 md:divide-x divide-zinc-200">
            {PLANS.map((plan) => (
              <div
                key={plan.key}
                className={`p-8 sm:p-10 flex flex-col ${plan.highlight ? "panel" : "bg-white"}`}
                data-testid={`pricing-card-${plan.key}`}
              >
                <div className="flex items-center justify-between mb-8">
                  <p className="font-display text-xl font-semibold text-zinc-900">{plan.name}</p>
                  {plan.highlight && <span className="eyebrow text-zinc-900">Most popular</span>}
                </div>
                <p className="font-display text-6xl font-semibold text-zinc-900 tracking-tight">
                  {plan.price}
                  <span className="text-base font-normal text-zinc-500 ml-1 tracking-normal">/ year</span>
                </p>
                <p className="text-zinc-600 mt-4 mb-10">{plan.limit}</p>
                <Link
                  to="/login"
                  className={`mt-auto inline-flex items-center justify-center h-11 rounded-md font-medium transition-colors ${
                    plan.highlight
                      ? "bg-[#303030] hover:bg-[#1A1A1A] text-white"
                      : "border border-zinc-300 text-zinc-900 hover:border-zinc-900"
                  }`}
                  data-testid={`pricing-cta-${plan.key}`}
                >
                  Start free trial
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <p className="eyebrow lg:col-span-3 pt-3">Every plan includes</p>
            <ul className="lg:col-span-9 grid grid-cols-1 sm:grid-cols-2 gap-x-10">
              {COMMON_FEATURES.map((feature) => (
                <li key={feature} className="flex items-center gap-3 text-zinc-700 py-3 border-b border-zinc-200">
                  <Check size={16} weight="bold" className="text-zinc-900 shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="border-t border-zinc-200">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="eyebrow mb-4">FAQ</p>
              <h2 className="font-display text-3xl sm:text-[40px] leading-[1.1] font-semibold text-zinc-900">
                Common questions.
              </h2>
            </div>
            <dl className="lg:col-span-8 border-t border-zinc-200">
              {FAQS.map((faq) => (
                <div key={faq.q} className="py-7 border-b border-zinc-200">
                  <dt className="font-display text-lg font-semibold text-zinc-900 mb-2">{faq.q}</dt>
                  <dd className="text-zinc-600 leading-relaxed">{faq.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
};

export default PricingPage;
